/* globals BaseUrl */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Events, handleAnalytics, setPagePropsStorage } from '@foxcorp/delta-web-analytics';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { handleSavedSearches, clearSavedSearches } from 'componentsV2/Filter/FilterHelper';
import Filter from 'componentsV2/Filter';
import classNames from 'classnames';
import Browse from './components/Browse';
import { fetchTrending, resetBrowse, selectTab, fetchNextPage, setFetching } from './BrowseActions';
import { fetchNextSearch } from './SearchActions';
import {
  getSelectedFilters,
  getSelectedResults,
  reduceBrowseResults,
} from './components/FindProcessor';
import useSearch from './hooks/useSearch';
import FindHeader from './components/FindHeader';
import Style from './Find.module.scss';

const Find = ({ account, accessToken, deviceId, blockReferrerAnalyticsTracking }) => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [fetchingSearch, setFetchingSearch] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [savedSearches, setSavedSearches] = useState([]);
  // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
  const [trackedAnalyticsReferrer, setTrackedAnalyticsReferrer] = useState(false);
  const inputRef = useRef(null);
  const previousItemsRef = useRef({});
  const previousSelectedFilterRef = useRef('');

  const {
    browseFilters,
    browseResults,
    fetching,
    searchFilters,
    searchResults,
    selectedTab,
  } = useSelector(state => state.find);

  const {
    search_header: headlineText,
    recentSearchesTitle,
    recentSearchesClearText,
  } = window.Language;

  const filters = useMemo(
    () =>
      getSelectedFilters({
        previousItemsRef,
        fetchingSearch,
        searchValue,
        searchFilters,
        browseFilters,
      }),
    [browseFilters, fetchingSearch, searchFilters, searchValue],
  );

  const results = useMemo(
    () =>
      getSelectedResults({
        fetchingSearch,
        searchValue,
        searchResults,
        browseResults,
        previousItemsRef,
      }),
    [browseResults, fetchingSearch, searchResults, searchValue],
  );

  const filterName = 'trending';
  const browseContext = useMemo(() => results?.[selectedTab], [results, selectedTab]);
  const items = useMemo(() => browseContext?.items ?? [], [browseContext]);
  const showBrowseTxt = !!(filters?.length > 0 && !searchValue && !isScrolled);
  const nextUrl = browseContext?.next;

  useEffect(() => {
    if (fetching || (searchValue && !searchCompleted)) return;
    const result = handleSavedSearches(searchValue, items);
    setSavedSearches(result);
  }, [items, fetching, searchValue, searchCompleted]);

  useEffect(() => {
    handleAnalytics({
      event: Events.FIND_SECTION_SEARCH,
      payload: {
        pageName: `find:landing`,
        pageType: `find:section-front`,
      },
    });
    handleAnalytics({
      callback: setPagePropsStorage,
      event: Events.SEARCH_STARTED,
      payload: {
        page_name: 'delta:web:find:landing',
        page_type: 'find:section-front',
      },
    });
  }, []);

  useEffect(() => {
    // this is needed to show the previously rendered data, till the time new search data is updated
    previousItemsRef.current = { results, filters };
  }, [results, filters]);

  useEffect(() => {
    if (filterName) {
      dispatch(selectTab(filterName));
    }
  }, [filterName, dispatch]);

  useEffect(() => {
    previousSelectedFilterRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    dispatch(setFetching(true));
    dispatch(fetchTrending({ accessToken, deviceId }));
    return () => {
      dispatch(resetBrowse());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleOnScroll = event => {
    const scrollPosition = event.target.documentElement.scrollTop;
    if (scrollPosition > 0) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    const itemLength = browseContext?.items?.length;
    if (itemLength && !nextUrl) {
      dispatch(setFetching(false));
    }
    if (itemLength === 0 && nextUrl) {
      dispatch(setFetching(true));
      dispatch(
        fetchNextPage({
          accessToken,
          deviceId,
          nextUrl,
          filterName: previousSelectedFilterRef.current,
        }),
      );
    }
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [accessToken, nextUrl, browseContext, deviceId, dispatch]);

  useEffect(() => {
    // set default selected tab to shows-all
    if (!searchValue) {
      dispatch(selectTab(filterName));
    }
  }, [dispatch, searchValue, filterName]);

  useSearch({
    accessToken,
    deviceId,
    inputRef,
    searchCount,
    searchValue,
    setFetchingSearch,
    setSearchCompleted,
  });

  const getNextPage = useCallback(
    next => {
      if (next && !fetching) {
        dispatch(setFetching(true));
        const fn = next?.includes('/trending') ? fetchNextPage : fetchNextSearch;
        dispatch(
          fn({
            accessToken,
            deviceId,
            nextUrl: next,
            filterName: previousSelectedFilterRef.current,
          }),
        );
      }
    },
    [accessToken, deviceId, dispatch, fetching],
  );

  const onSearchClear = () => {
    setFetchingSearch(false);
    setSearchCompleted(false);
    setSearchValue('');
    dispatch(selectTab(filterName));
  };

  const onSearchInputChange = event => {
    const val = event?.target?.value;
    setSearchValue(val);
    dispatch(setFetching(true));

    if (val && !fetchingSearch) {
      setFetchingSearch(true);
    }

    if (val && searchCompleted) {
      setSearchCount(searchCount => searchCount + 1);
      setSearchCompleted(false);
    }

    if (!val) {
      onSearchClear();
    }
  };
  const SEOOverride = {
    robots: 'noindex, nofollow',
    canonicalUrl: `${BaseUrl}/find/`,
    title: 'Search | Fox Nation',
  };

  const handleClearAll = () => {
    handleAnalytics({
      event: Events.BUTTON_LINK_SELECTED,
      payload: {
        page_button_text: 'Clear All',
        page_name: `delta:web:find:landing`,
        page_type: `find:section-front`,
      },
    });
    clearSavedSearches();
    setSavedSearches([]);
  };

  return (
    <section className={classNames([Style.container, isScrolled && Style.scrolled])}>
      {setHelmetTags(SEOOverride)}
      <FindHeader
        isScrolled={isScrolled}
        onSearchInputChange={onSearchInputChange}
        onSearchClear={onSearchClear}
        Style={Style}
        searchValue={searchValue}
        inputRef={inputRef}
        fetchingSearch={fetchingSearch}
        filterName={filterName}
        filters={filters}
        showBrowseTxt={showBrowseTxt}
        setSearchValue={setSearchValue}
        setFetchingSearch={setFetchingSearch}
        searchResults={items}
      />
      {savedSearches.length > 0 && !searchValue && (
        <div className={Style.recentSearchContainer}>
          <div className={classNames(Style.findBrowseTxtContainer, Style.recentResultsContainer)}>
            <h3 className={Style.recentSearchesTitle}>{recentSearchesTitle}</h3>
            <div onClick={handleClearAll} className={Style.clearAll}>
              {recentSearchesClearText}
            </div>
          </div>
          <Filter
            isScrolled={isScrolled}
            filters={savedSearches}
            isCarousel
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setFetchingSearch={setFetchingSearch}
            theme={Style}
          />
        </div>
      )}
      {!searchValue && !isScrolled && (
        <div
          className={classNames(
            savedSearches.length > 0 ? null : Style.noResultsTitleContainer,
            showBrowseTxt,
          )}
        >
          <h1 className={Style.findBrowseTxt}>{headlineText}</h1>
        </div>
      )}
      <Browse
        account={account}
        fetching={fetching}
        fetchingSearch={fetchingSearch}
        items={items}
        loadMore={getNextPage}
        next={browseContext?.next}
        searchProps={{
          searchCompleted,
          searchCount,
          searchValue,
          trackedAnalyticsReferrer,
          setTrackedAnalyticsReferrer,
          blockReferrerAnalyticsTracking,
        }}
        shouldRender
        type={results?.[selectedTab]?.type}
        Style={savedSearches.length > 0 && !searchValue ? Style : null}
      />
    </section>
  );
};

Find.fetchData = ({ store, accessToken, params }) =>
  store.dispatch(dispatch =>
    dispatch(fetchTrending(accessToken)).then(async response => {
      const data = response?.payload?.data;
      const BrowseResults = reduceBrowseResults(data?.result);
      const filter = 'trending';
      dispatch(selectTab(filter));
      const nextUrl = BrowseResults?.[filter]?.next;
      if (nextUrl)
        return dispatch(
          fetchNextPage({
            accessToken,
            nextUrl,
            filterName: filter,
          }),
        );

      return response;
    }),
  );

export default Find;

Find.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  blockReferrerAnalyticsTracking: PropTypes.bool,
  deviceId: PropTypes.string,
};
